import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteAgencyMedia,
  deleteTalentResume,
  extractResumeInfo,
  getAgencyProfile,
  getCertificates,
  getFrameWorks,
  getFrameWorksFromSpheres,
  getIndustries,
  getLangFromSpheres,
  getLanguages,
  getRateSetup,
  getTalentResume,
  getTechSpheres,
  getUploadUrl,
  saveAgencyDetails,
  saveRateSetup,
  saveTalentDetails,
  uploadCompanyVideo,
  uploadResumeForAutofill,
  uploadTalentVideo,
  uploadToS3,
  validateExistingEmail,
  validateVimeoVideo,
} from '../api/agency.service';
import { QUERY_KEYS } from '../querykey';
import toast from 'react-hot-toast';
import { DeleteMediaVariables } from '../../types/app';


export const useSaveAgencyDetails = (options = {}) => {
  const { mutate, isPending } = useMutation({
    mutationFn: saveAgencyDetails,
    mutationKey: [QUERY_KEYS.SAVE_AGENCY_DETAILS],
    ...options,
  });
  return { mutate, isPending };
};

export const useGetAgencyProfile = (options = {}) => {
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: [QUERY_KEYS.GET_AGENCY_PROFILE],
    queryFn: getAgencyProfile,
    ...options,
  });

  return { agencyDetails: data?.data, isPending, isError, error, refetch };
};

export const useSaveTalentDetails = (options = {}) => {
  const { mutate, mutateAsync, isPending } = useMutation({
    mutationFn: saveTalentDetails,
    mutationKey: [QUERY_KEYS.SAVE_TALENT_DETAILS],
    ...options,
  });

  return { mutate, mutateAsync, isPending };
};

export const useGetLanguages = (options = {}) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [QUERY_KEYS.GET_LANGUAGES],
    queryFn: getLanguages,
    ...options,
  });

  return { languages: data?.data, isPending, isError, error };
};

export const useGetIndustries = (options = {}) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [QUERY_KEYS.GET_INDUSTRIES],
    queryFn: getIndustries,
    ...options,
  });

  return { industries: data?.data, isPending, isError, error };
};

export const useGetCertificates = (options = {}) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [QUERY_KEYS.GET_CERTIFICATES],
    queryFn: getCertificates,
    ...options,
  });

  return { certificates: data?.data, isPending, isError, error };
};

export const useGetTechSpheres = (options = {}) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [QUERY_KEYS.GET_TECH_SPHERES],
    queryFn: getTechSpheres,
    ...options,
  });

  return { techSpheres: data?.data, isPending, isError, error };
};

export const useGetLangFromSpheres = (selectedSphereId: string) => {
  const queryKey = [QUERY_KEYS.GET_LANG_FROM_SPHERES, selectedSphereId];
  const { data, isPending, isError, error } = useQuery({
    queryKey: queryKey,
    queryFn: () => getLangFromSpheres([selectedSphereId]),
    enabled: !!selectedSphereId,
  });

  return {
    languageSpheres: data?.data,
    isFetchingLanguages: isPending,
    isError,
    error,
  };
};

export const useGetFrameworksFromSpheres = (selectedSphereId: string) => {
  const queryKey = [QUERY_KEYS.GET_FRAMEWORKS_FROM_SPHERES, selectedSphereId];
  const { data, isPending, isError, error } = useQuery({
    queryKey: queryKey,
    queryFn: () => getFrameWorksFromSpheres([selectedSphereId]),
    enabled: !!selectedSphereId,
  });

  return {
    frameworksSpheres: data?.data,
    isFetchingFrameworks: isPending,
    isError,
    error,
  };
};

export const useGetFrameWorks = (options = {}) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [QUERY_KEYS.GET_FRAMEWORKS],
    queryFn: getFrameWorks,
    ...options,
  });

  return { frameworks: data?.data, isPending, isError, error };
};

export const useUploadTalentVideo = (options = {}) => {
  const { mutate, isPending } = useMutation({
    mutationFn: uploadTalentVideo,
    mutationKey: [QUERY_KEYS.UPLOAD_TALENT_VIDEO],
    ...options,
  });
  return { mutate, isPending };
};

export const useSaveRateSetup = (options = {}) => {
  const { mutate, isPending } = useMutation({
    mutationFn: saveRateSetup,
    mutationKey: [QUERY_KEYS.AGENCY_RATE_SETUP],
    ...options,
  });
  return { mutate, isPending };
};

export const useValidateVimeoVideo = (options = {}) => {
  const { mutate, error } = useMutation({
    mutationFn: validateVimeoVideo,
    mutationKey: [QUERY_KEYS.VALIDATE_VIMEO_VIDEO],
    ...options,
  });

  return {
    validateVideo: mutate,
    error,
  };
};

export const useGetRateSetup = (associateId: string | null) => {
  const queryKey = [QUERY_KEYS.GET_RATE_SETUP, associateId];

  const { data, isPending, isError, error, isLoading } = useQuery({
    queryKey: queryKey,

    queryFn: () => getRateSetup(associateId!),

    enabled: !!associateId,
  });

  const isLoadingState = associateId ? isPending : isLoading;

  return {
    rateSetup: data?.data,
    isPending: isLoadingState,
    isError,
    error,
  };
};

const S3_BUCKET_PREFIX = process.env.REACT_APP_RESUME_EXTRACTION;

export const useResumeUploadFlow = (options = {}) => {
  return useMutation({
    mutationKey: [QUERY_KEYS.RESUME_UPLOAD_FLOW],

    mutationFn: async ({
      file,
      associateId,
    }: {
      file: File;
      associateId: number;
    }) => {
      // STEP 1: Upload & Get UUID
      const uploadResponse = await uploadResumeForAutofill(file, associateId);

      // Extract UUID from response

      const uuid = uploadResponse?.data?.uuid || uploadResponse?.uuid;

      console.log('Upload Response:', uploadResponse);

      if (!uuid) {
        throw new Error('Upload successful but failed to retrieve UUID');
      }

      // STEP 2: Construct the Full S3 URI
      // The backend expects: s3://workfall-resume-extractor-qa/<uuid>
      const fullS3Uri = `${S3_BUCKET_PREFIX}/${uuid}`;

      console.log('Extracting from:', fullS3Uri);
      // 2. Upload to S3

      // We wait for this to finish before moving to step 3

      // await uploadToS3(uploadUrl, file);

      // Call the extraction API with the full S3 URI
      const extractionResult = await extractResumeInfo({
        objectKey: fullS3Uri, // Pass the full string here
      });

      return extractionResult;
    },
    onError: (error: any) => {
      console.error('Resume processing failed:', error);
      toast.error(error?.response?.data?.message || 'Failed to process resume');
    },
    ...options,
  });
};

export const useDeleteTalentResume = (options = {}) => {
  const { mutate, isPending } = useMutation({
    mutationFn: deleteTalentResume,
    mutationKey: [QUERY_KEYS.DELETE_TALENT_RESUME],
    ...options,
  });
  return { mutate, isPending };
};

export const useGetTalentResume = (id: number, options = {}) => {
  const query = useQuery({
    queryKey: [QUERY_KEYS.GET_TALENT_RESUME, id],
    queryFn: () => getTalentResume(id),
    enabled: !!id,
    ...options,
  });

  return query;
};

export const useValidateExisingEmail = (options = {}) => {
  const { mutate, isPending } = useMutation({
    mutationFn: validateExistingEmail,
    mutationKey: [QUERY_KEYS.VALIDATE_EXISTING_EMAIL],
    ...options,
  });

  return { mutate, isPending };
};

export const useDeleteAgencyMedia = (options?: any) => {
  const queryClient = useQueryClient();

  // Updated with Generics: <Response, Error, Variables>
  return useMutation<any, Error, DeleteMediaVariables>({
    mutationFn: (payload) => deleteAgencyMedia(payload),

    onSuccess: (res) => {
      toast.success(res?.message || 'Media removed successfully');

      // Refresh agency details
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_AGENCY_PROFILE],
      });

      if (options?.onSuccess) options.onSuccess(res);
    },

    onError: (error: any) => {
      console.error('Delete Media Error:', error);
      toast.error(error?.response?.data?.message || 'Failed to delete media.');
      if (options?.onError) options.onError(error);
    },
    ...options,
  });
};


export const useUploadCompanyVideo = (options = {}) => {
  const { mutate, isPending } = useMutation({
    mutationFn: uploadCompanyVideo,
    mutationKey: [QUERY_KEYS.UPLOAD_AGENCY_VIDEO],
    ...options,
  });
  return { mutate, isPending };
};
